import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../hooks/useTheme";

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Check if the user was redirected here due to session expiration
  useEffect(() => {
    const authExpired = localStorage.getItem("auth_expired");
    if (authExpired === "true") {
      setSessionExpired(true);
      // Clear the flag
      localStorage.removeItem("auth_expired");
    }
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    setSessionExpired(false); // Clear the message when user tries to log in
    console.log("Starting Auth0 login...");
    try {
      console.log("Calling login function...");
      await login();
      console.log("Login function completed");
    } catch (err: any) {
      setLoading(false);
      console.error("Login failed:", err);
      console.error("Error details:", err.message, err.stack);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-16">
      {/* Theme Toggle */}
      <div className="flex justify-end mb-4">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "light" ? (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          )}
        </button>
      </div>

      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-8">
        <h2 className="text-2xl font-bold text-center text-zinc-800 dark:text-zinc-100 mb-6">
          Welcome to Clozeon
        </h2>

        {sessionExpired && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Session Expired
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Your session has expired for security reasons. Please sign in
                  again to continue.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <p className="text-zinc-600 dark:text-zinc-400">
            Sign in with your account to continue
          </p>
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full bg-cyan-600 dark:bg-cyan-700 text-white py-3 px-4 rounded-md hover:bg-cyan-700 dark:hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span>Continue with Auth0</span>
            </>
          )}
        </button>

        <div className="mt-6 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
            Secure authentication powered by Auth0
          </p>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            Clozeon is an interactive learning tool for mastering Biblical Greek
            through contextual vocabulary practice and verb parsing exercises.
            We only use your user account to store your custom collections,
            progress, and settings. We will never share or sell your data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
